import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Eye, Check, Sparkles } from '../components/Icons.jsx';

const scenarios = [
  {
    setup: 'A clinic surveys 120 patients. 70 take medication A. 55 take medication B. 30 take both.',
    regions: [
      { label: 'A only', answer: 40, working: '70 − 30 (both)' },
      { label: 'B only', answer: 25, working: '55 − 30 (both)' },
      { label: 'Both A and B', answer: 30, working: 'Given' },
      { label: 'Neither', answer: 25, working: '120 − 40 − 25 − 30' }
    ],
    question: 'How many take medication A but not B?',
    answer: '40',
    common: 'Students subtract the wrong way — they take 70 - 55 = 15 instead of 70 - 30 = 40. The "both" overlap is what comes out of "A only", not the smaller set.',
    script: '"Draw two circles overlapping. The overlap is 30. Now — how many take A in total? 70. How many of those 70 are also in the overlap? 30. So A-only is 70 minus 30."'
  },
  {
    setup: 'A medical school admits 200 students. 140 hold an A* in Chemistry. 110 hold an A* in Biology. 85 hold both.',
    regions: [
      { label: 'Chem A* only', answer: 55, working: '140 − 85' },
      { label: 'Bio A* only', answer: 25, working: '110 − 85' },
      { label: 'Both A* Chem & Bio', answer: 85, working: 'Given' },
      { label: 'Neither', answer: 35, working: '200 − 55 − 25 − 85' }
    ],
    question: 'How many do not hold an A* in either subject?',
    answer: '35',
    common: 'Students forget the "neither" region exists. They sum the three known regions to get 165 and miss that 200 − 165 = 35 must go somewhere.',
    script: '"Sketch the rectangle around both circles. Anything outside both circles but inside the rectangle is the neither region. Always make the rectangle visible — that\'s where students lose marks."'
  },
  {
    setup: 'A revision app tracks 300 candidates. 180 use the UCAT module. 150 use the BMAT module. 90 use the personal statement module. 70 use UCAT and BMAT. 50 use UCAT and PS. 40 use BMAT and PS. 30 use all three.',
    regions: [
      { label: 'UCAT only', answer: 90, working: '180 − 70 − 50 + 30' },
      { label: 'BMAT only', answer: 70, working: '150 − 70 − 40 + 30' },
      { label: 'PS only', answer: 30, working: '90 − 50 − 40 + 30' },
      { label: 'All three', answer: 30, working: 'Given' }
    ],
    question: 'How many use UCAT only?',
    answer: '90',
    common: 'Three-circle Venns: students subtract the pair overlaps but forget to add back the triple overlap. Each pair overlap already excludes the centre once — so "UCAT only" = 180 − (UCAT∩BMAT) − (UCAT∩PS) + (all three).',
    script: '"Always start from the centre and work outwards. Fill the 30 first. Then subtract from each pair to get the pair-only region. Then subtract everything you\'ve drawn from each single set. It\'s the order that saves you 20 seconds."'
  },
  {
    setup: 'A study of 80 medical students measured caffeine intake and sleep quality. 50 reported high caffeine. 35 reported poor sleep. 25 reported both.',
    regions: [
      { label: 'High caffeine only', answer: 25, working: '50 − 25' },
      { label: 'Poor sleep only', answer: 10, working: '35 − 25' },
      { label: 'Both', answer: 25, working: 'Given' },
      { label: 'Neither', answer: 20, working: '80 − 25 − 10 − 25' }
    ],
    question: 'What proportion of students reported neither high caffeine nor poor sleep?',
    answer: '20 / 80 = 25%',
    common: 'Students compute the count but forget the question asked for a proportion. UCAT often double-loads questions like this — count, then percentage.',
    script: '"Two-step questions are where the timing dies. Have the student write the count and the proportion side by side every time, even if only one is asked."'
  }
];

const STUDENT_SEED_BASE = typeof window !== 'undefined' ? `${window.location.origin}/student-puzzle?` : '';

export default function TutorVennLive() {
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(new Set());
  const [showAnswer, setShowAnswer] = useState(false);
  const [showTutor, setShowTutor] = useState(true);
  const [copied, setCopied] = useState(false);

  const s = scenarios[idx];

  const newScenario = () => {
    const next = (idx + 1 + Math.floor(Math.random() * (scenarios.length - 1))) % scenarios.length;
    setIdx(next);
    setRevealed(new Set());
    setShowAnswer(false);
  };
  const revealRegion = (i) => setRevealed(new Set([...revealed, i]));
  const studentLink = useMemo(() => `${STUDENT_SEED_BASE}seed=venn-${idx}`, [idx]);
  const copyLink = () => {
    navigator.clipboard?.writeText(studentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        padding: '14px 18px', borderBottom: '1px solid var(--border)', background: '#fff'
      }}>
        <Link to="/tutor-portal" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-2)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
          <ArrowLeft width="16" height="16" />Tutor portal
        </Link>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-deep)' }}>Venn Reasoning · Live</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowTutor(!showTutor)} style={btnSecondary}>
            {showTutor ? 'Hide tutor panel' : 'Show tutor panel'}
          </button>
          <button onClick={copyLink} style={btnSecondary}>
            {copied ? 'Copied ✓' : 'Copy student link'}
          </button>
          <button onClick={newScenario} style={btnPrimary}>
            <Sparkles width="14" height="14" />New scenario
          </button>
        </div>
      </header>

      <div className="tutor-shell" style={{ display: 'grid', gridTemplateColumns: showTutor ? '1fr 360px' : '1fr', gap: 0, minHeight: 'calc(100vh - 60px)' }}>
        <main style={{ padding: 'clamp(20px, 4vw, 36px) clamp(18px, 4vw, 48px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
            Scenario {idx + 1} of {scenarios.length}
          </div>
          <h1 style={{ fontSize: 'clamp(20px, 3.6vw, 30px)', fontWeight: 700, lineHeight: 1.3, margin: '0 0 24px', maxWidth: 820 }}>{s.setup}</h1>

          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, maxWidth: 820 }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>Question</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--brand-deep)' }}>{s.question}</div>
          </div>

          <div style={{ marginBottom: 24, maxWidth: 820 }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 10 }}>Reveal each region as you work through it</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {s.regions.map((r, i) => {
                const isOpen = revealed.has(i);
                return (
                  <button
                    key={i}
                    onClick={() => revealRegion(i)}
                    style={{
                      padding: '16px 18px', textAlign: 'left', cursor: 'pointer',
                      border: `1px solid ${isOpen ? 'var(--brand-purple)' : 'var(--border)'}`,
                      borderRadius: 12,
                      background: isOpen ? 'var(--accent-soft)' : '#fff',
                      transition: 'all .15s ease'
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>{r.label}</div>
                    {isOpen
                      ? <>
                          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--brand-deep)' }}>{r.answer}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.working}</div>
                        </>
                      : <div style={{ fontSize: 16, color: 'var(--text-muted)' }}>Tap to reveal →</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ maxWidth: 820 }}>
            {showAnswer
              ? <div style={{ padding: '20px 24px', background: 'var(--brand-deep)', color: '#fff', borderRadius: 14 }}>
                  <div style={{ fontSize: 13, opacity: 0.7, fontWeight: 600, marginBottom: 4 }}>Final answer</div>
                  <div style={{ fontSize: 32, fontWeight: 700 }}>{s.answer}</div>
                </div>
              : <button onClick={() => setShowAnswer(true)} style={{ ...btnPrimary, fontSize: 16, padding: '12px 22px' }}>
                  Reveal final answer
                </button>}
          </div>
        </main>

        {showTutor && (
          <aside className="tutor-aside" style={{ background: 'var(--card-soft)', borderLeft: '1px solid var(--border)', padding: 24, fontSize: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Tutor only — students don't see this
            </div>

            <h3 style={{ margin: '4px 0 6px', fontSize: 15 }}>Common mistake</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-2)' }}>{s.common}</p>

            <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>30-second script</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontStyle: 'italic' }}>{s.script}</p>

            <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Student companion link</h3>
            <p style={{ margin: '0 0 6px', color: 'var(--text-2)' }}>Paste into Zoom/Teams chat. Student gets the same scenario on their device.</p>
            <code style={{ display: 'block', padding: '8px 10px', background: '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, wordBreak: 'break-all', color: 'var(--text-2)' }}>{studentLink}</code>
          </aside>
        )}
      </div>
    </div>
  );
}

const btnBase = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 600,
  borderRadius: 10, cursor: 'pointer', border: '1px solid transparent'
};
const btnPrimary = { ...btnBase, background: 'var(--brand-deep)', color: '#fff' };
const btnSecondary = { ...btnBase, background: '#fff', border: '1px solid var(--border)', color: 'var(--text-2)' };
